// @ts-check
// Aba WhatsApp de Campanhas > Modelos (spec/whatsapp.md seção 3.7 A; contrato 09 de campanhas de WhatsApp, seção 2):
// o que é marketing e o que é comunicado explicado no topo, "Novo modelo" e "Sincronizar com a Meta" para quem tem
// Campanhas de WhatsApp ou administra a escola, busca e filtro de número na URL, e os modelos em três seções:
// Marketing, Comunicados (utilidade de uso CAMPAIGN) e, recolhida, Modelos de atendimento (utilidade de conversa,
// com "Usar em comunicados"). Um modelo aparece uma vez por conta do WhatsApp Business ("Números: A, B").
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { listChannels } from '../../data/conversations.js';
import { listWhatsappTemplates, syncWhatsappTemplates, updateWhatsappTemplateUsage } from '../../data/integrations.js';
import { getErrorMessage } from '../../data/api-client.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import {
  campaignTemplateHref, campaignTemplateNewHref, canManageCampaignTemplates, channelLabel, dedupeTemplatesByWaba, filterTemplates, templatesPageState,
} from '../../lib/whatsapp-templates.js';
import { templateGroupsFor } from '../../../../shared/whatsapp/template-rules.js';
import { WhatsappTemplateCard } from '../settings/whatsapp-templates/whatsapp-template-card.js';
import { isTemplateInReview, useReviewRefresh } from './use-review-refresh.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 */

const CAMPAIGN_TEMPLATES_RETURN_PATH = '/campaigns/templates?tab=whatsapp';
const EMPTY_TEXT_KEYS = /** @type {Record<string, string>} */ ({
  MARKETING: 'whatsappTemplates.campaignTab.emptyMarketing',
  ANNOUNCEMENT: 'whatsappTemplates.campaignTab.emptyAnnouncement',
  SERVICE: 'whatsappTemplates.campaignTab.emptyService',
});

/** @param {MouseEvent} event */
const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** Link interno que navega sem recarregar e deixa Ctrl/Cmd e o clique do meio abrirem outra aba. @param {string} href */
export function spaLinkHandler(href) {
  return (/** @type {MouseEvent} */ event) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    navigate(href);
  };
}

/** Caixa do topo que explica um tipo de modelo. @param {{ kind: 'marketing' | 'announcement', icon: string, title: string, text: string }} props */
function ExplainBox({ kind, icon, title, text }) {
  return html`<div class="wa-templates-explain-box" data-kind=${kind}>
    <span class="wa-templates-explain-icon" aria-hidden="true"><${Icon} name=${icon} size=${18} /></span>
    <div><h3>${title}</h3><p>${text}</p></div>
  </div>`;
}

export function WhatsappTemplatesTab() {
  const canManage = canManageCampaignTemplates([
    ...(hasCurrentPermissionFlag('WORKSPACE') ? ['WORKSPACE'] : []),
    ...(hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS') ? ['WHATSAPP_CAMPAIGNS'] : []),
  ]);
  const isAdmin = hasCurrentPermissionFlag('WORKSPACE');
  const [channels, setChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [templates, setTemplates] = useState(/** @type {WhatsappTemplate[] | null} */ (null));
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isServiceOpen, setIsServiceOpen] = useState(false);
  const [changingId, setChangingId] = useState(/** @type {string | null} */ (null));
  const channelFilter = searchParamsState.value.get('number');

  const loadTemplates = () => listWhatsappTemplates({ withUses: true }).then((list) => {
    setTemplates(list);
    setLoadFailed(false);
  });
  const load = () => {
    setLoadFailed(false);
    Promise.all([listChannels(), listWhatsappTemplates({ withUses: true })])
      .then(([channelData, list]) => {
        setChannels(channelData.channels.filter((channel) => channel.type === 'WHATSAPP'));
        setTemplates(list);
      })
      .catch((error) => {
        // a falha nunca vira "nenhum número" nem "nenhum modelo"
        showErrorSnackbar(error);
        setLoadFailed(true);
      });
  };
  useEffect(load, []);

  const review = useReviewRefresh((templates ?? []).some(isTemplateInReview), () => loadTemplates().catch(() => null));

  const whatsappChannels = channels ?? [];
  const channelsWithWaba = whatsappChannels.filter((channel) => Boolean(channel.settings?.wabaId));
  const channelsById = new Map(whatsappChannels.map((channel) => [channel.id, channel]));
  const pageState = templatesPageState({ channels: channels === null ? null : whatsappChannels, templates, loadFailed });
  const newHref = campaignTemplateNewHref({ channelId: channelFilter, from: CAMPAIGN_TEMPLATES_RETURN_PATH });

  const sync = async () => {
    if (channelsWithWaba.length === 0) return;
    setIsSyncing(true);
    /** @type {Array<{ channel: Channel, error: string }>} */
    const failures = [];
    // um pedido por número, em sequência, como em Configurações
    for (const channel of channelsWithWaba) {
      try {
        await syncWhatsappTemplates(channel.id);
      } catch (error) {
        failures.push({ channel, error: getErrorMessage(error) });
      }
    }
    setIsSyncing(false);
    if (failures.length === 0) showSuccessSnackbar(t('whatsappTemplates.sync.done'));
    else {
      enqueueSnackbar({
        variant: 'error',
        message: t('whatsappTemplates.sync.partial', { ok: channelsWithWaba.length - failures.length, total: channelsWithWaba.length, number: channelLabel(failures[0].channel), error: failures[0].error }),
      });
    }
    review.restart();
    await loadTemplates().catch(showErrorSnackbar);
  };

  const changeUsage = async (/** @type {WhatsappTemplate} */ template, /** @type {'CAMPAIGN' | 'CONVERSATION'} */ usage) => {
    setChangingId(template.id);
    try {
      await updateWhatsappTemplateUsage(template.id, usage);
      showSuccessSnackbar(t(usage === 'CAMPAIGN' ? 'whatsappTemplates.usage.changedToAnnouncement' : 'whatsappTemplates.usage.changedToService'));
      await loadTemplates();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setChangingId(null);
    }
  };

  const actions = canManage && pageState === 'READY'
    ? html`<div class="templates-header-actions">
        <${Button} title=${isSyncing ? t('whatsappTemplates.actions.syncing') : t('whatsappTemplates.actions.sync')} icon="IconRefresh" variant="secondary" size="small"
          isLoading=${isSyncing} disabled=${channelsWithWaba.length === 0} onClick=${sync} />
        <${Button} title=${t('whatsappTemplates.actions.new')} icon="IconPlus" accent="blue" size="small" disabled=${channelsWithWaba.length === 0}
          onClick=${() => navigate(newHref)} />
      </div>`
    : null;

  /** @param {{ template: WhatsappTemplate, channelIds: string[] }} item @param {string} groupKey */
  const renderCard = (item, groupKey) => {
    const labels = item.channelIds.map((channelId) => channelLabel(channelsById.get(channelId))).filter(Boolean);
    const usageAction = !canManage
      ? null
      : groupKey === 'SERVICE'
        ? html`<${Button} title=${t('whatsappTemplates.usage.toAnnouncement')} icon="IconSpeakerphone" variant="secondary" size="small"
            isLoading=${changingId === item.template.id} onClick=${() => changeUsage(item.template, 'CAMPAIGN')} />`
        : groupKey === 'ANNOUNCEMENT'
          ? html`<${Button} title=${t('whatsappTemplates.usage.toService')} icon="IconArrowBackUp" variant="tertiary" size="small"
              isLoading=${changingId === item.template.id} onClick=${() => changeUsage(item.template, 'CONVERSATION')} />`
          : null;
    return html`<${WhatsappTemplateCard} key=${item.template.id} template=${item.template} canManage=${false}
      numberLabel=${item.channelIds.length === 1 && whatsappChannels.length > 1 ? labels[0] ?? '' : ''}
      numbersLabel=${item.channelIds.length > 1 ? t('whatsappTemplates.numbers', { numbers: labels.join(', ') }) : null}
      href=${campaignTemplateHref(item.template.id)} actions=${usageAction} />`;
  };

  /** @param {{ key: string, templates: WhatsappTemplate[] }} group */
  const renderSection = (group) => {
    const items = dedupeTemplatesByWaba(group.templates);
    const isCollapsible = group.key === 'SERVICE';
    const isOpen = !isCollapsible || isServiceOpen || Boolean(search.trim());
    const title = group.key === 'SERVICE' ? t('whatsappTemplates.campaignTab.serviceSection') : t(`whatsappTemplates.groups.${group.key}`);
    const headingId = `wa-templates-section-${group.key}`;
    return html`<section key=${group.key} class="wa-templates-section" aria-labelledby=${headingId} data-group=${group.key}>
      <div class="wa-templates-section-header">
        ${isCollapsible
          ? html`<h3 id=${headingId}><button type="button" class="wa-templates-section-toggle" aria-expanded=${isOpen ? 'true' : 'false'}
              onClick=${() => setIsServiceOpen(!isOpen)}>
              <${Icon} name="IconChevronRight" size=${16} />${title}<span class="wa-templates-section-count">${items.length}</span>
            </button></h3>`
          : html`<h3 id=${headingId}>${title}<span class="wa-templates-section-count">${items.length}</span></h3>`}
        <p>${group.key === 'SERVICE' ? t('whatsappTemplates.campaignTab.serviceSectionHelp') : t(`whatsappTemplates.groupHelp.${group.key}`)}</p>
      </div>
      ${!isOpen
        ? null
        : items.length === 0
          ? html`<p class="wa-templates-section-empty">
              <span>${search.trim() || channelFilter ? t('whatsappTemplates.empty.noResults') : t(EMPTY_TEXT_KEYS[group.key] ?? 'whatsappTemplates.empty.noResults')}</span>
              ${canManage && group.key !== 'SERVICE' && !search.trim() && channelsWithWaba.length > 0
                ? html`<${Button} title=${t('whatsappTemplates.actions.new')} icon="IconPlus" variant="secondary" size="small"
                    onClick=${() => navigate(campaignTemplateNewHref({ channelId: channelFilter, from: CAMPAIGN_TEMPLATES_RETURN_PATH, kind: group.key === 'ANNOUNCEMENT' ? 'ANNOUNCEMENT' : 'MARKETING' }))} />`
                : null}
            </p>`
          : html`<ul class="templates-list">${items.map((item) => renderCard(item, group.key))}</ul>`}
    </section>`;
  };

  const renderBody = () => {
    if (pageState === 'LOADING') {
      return html`<div class="templates-skeleton" aria-busy="true"><${Skeleton} height=${160} radius=${8} /><${Skeleton} height=${160} radius=${8} /><${Skeleton} height=${160} radius=${8} /></div>`;
    }
    if (pageState === 'LOAD_FAILED') {
      return html`<div class="integration-empty templates-empty" role="alert"><p>${t('whatsappTemplates.empty.loadFailed')}</p>
        <${Button} title=${t('whatsappTemplates.actions.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`;
    }
    if (pageState === 'NO_CHANNEL') {
      return html`<div class="integration-empty templates-empty">
        <p>${isAdmin ? t('whatsappTemplates.campaignTab.noChannelAdmin') : t('whatsappTemplates.campaignTab.noChannelMember')}</p>
        ${isAdmin ? html`<${Button} title=${t('whatsappTemplates.actions.goToChannels')} icon="IconPlugConnected" variant="secondary" size="small" onClick=${() => navigate('/settings/channels')} />` : null}
      </div>`;
    }
    const filtered = filterTemplates(templates ?? [], { search, channelId: channelFilter });
    // autenticação (OTHER) não entra nas campanhas
    const groups = templateGroupsFor(filtered, 'SETTINGS').filter((group) => group.key !== 'OTHER');
    return html`
      ${loadFailed
        ? html`<div class="integration-empty templates-empty" role="alert"><p>${t('whatsappTemplates.empty.loadFailed')}</p>
            <${Button} title=${t('whatsappTemplates.actions.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`
        : null}
      ${groups.map(renderSection)}
    `;
  };

  return html`<div class="wa-templates-tab" data-testid="whatsapp-templates-tab">
    <div class="wa-templates-explain">
      <${ExplainBox} kind="marketing" icon="IconSpeakerphone" title=${t('whatsappTemplates.campaignTab.marketingTitle')} text=${t('whatsappTemplates.campaignTab.marketingHelp')} />
      <${ExplainBox} kind="announcement" icon="IconSchool" title=${t('whatsappTemplates.campaignTab.announcementTitle')} text=${t('whatsappTemplates.campaignTab.announcementHelp')} />
    </div>
    ${!canManage ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} /><span>${t('whatsappTemplates.campaignTab.readOnlyNote')}</span></p>` : null}
    ${pageState === 'READY'
      ? html`<div class="wa-templates-bar">
          <div class="templates-toolbar">
            <label class="data-model-search templates-search">
              <${Icon} name="IconSearch" size=${16} />
              <input class="text-input" type="search" value=${search} placeholder=${t('whatsappTemplates.search.placeholder')} aria-label=${t('whatsappTemplates.campaignTab.searchLabel')}
                onInput=${(/** @type {Event} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
            </label>
            ${whatsappChannels.length > 1
              ? html`<label class="templates-number-filter">
                  <span class="visually-hidden-text">${t('whatsappTemplates.filters.channel')}</span>
                  <select class="text-input integration-select" value=${channelFilter ?? ''} aria-label=${t('whatsappTemplates.filters.channel')}
                    onChange=${(/** @type {Event} */ event) => updateSearchParams({ number: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                    <option value="">${t('whatsappTemplates.filters.allChannels')}</option>
                    ${whatsappChannels.map((channel) => html`<option key=${channel.id} value=${channel.id} selected=${channel.id === channelFilter}>${channelLabel(channel)}</option>`)}
                  </select>
                </label>`
              : null}
          </div>
          ${actions}
        </div>`
      : null}
    ${renderBody()}
  </div>`;
}
